import React, { useState, useEffect } from 'react'
import { T, Spinner, Empty, VerdictChip, FTSY_VLEVEL, vlevelColor, Avatar, SearchBar, searchMatch } from '../ui'
import { getNFLFantasy } from '../api'
import CascadePanel from './CascadePanel'
import LineupPanel from './LineupPanel'
import MatchupPanel from './MatchupPanel'
import DraftPanel from './DraftPanel'
import { teamNick } from '../plain'

// Fantasy — the prop model re-scored for lineups. Projected fantasy points per
// player with a start/sit VERDICT and a green-check case (volume, red-zone role,
// matchup). Tap a row to open the case; tap ⇄ on two to Start/Sit compare.

const SCORING = [['ppr', 'PPR'], ['half', 'Half'], ['std', 'Std']]
const POSES = ['FLEX', 'QB', 'RB', 'WR', 'TE']
const gradeCol = g => g === 'A' ? T.green : g === 'B' ? T.mint : g === 'C' ? T.amber : g === 'D' ? T.red : T.muted
const ptsCol = p => p >= 18 ? T.green : p >= 12 ? T.mint : p >= 8 ? T.amber : T.muted2

// verdict text colour (readable on the card) — the filled "strong" level uses a
// dark ink for its chip, so swap to green when it's used as text.
const verdictText = v => { const c = vlevelColor(FTSY_VLEVEL[v]); return c === '#04140C' ? T.green : c }
const toneMark = t => t === 'good' ? { i: '✓', c: T.green } : t === 'bad' ? { i: '✕', c: T.red } : { i: '•', c: T.muted }

function CaseList({ items }) {
  if (!items || !items.length) return null
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginTop: 11 }}>
      {items.map((c, i) => {
        const tm = toneMark(c.tone)
        return (
          <div key={i} style={{ display: 'flex', gap: 9, alignItems: 'baseline' }}>
            <span style={{ color: tm.c, fontWeight: 900, fontSize: 12, width: 12, flexShrink: 0, textAlign: 'center' }}>{tm.i}</span>
            <div style={{ minWidth: 0 }}>
              <span style={{ fontSize: 12.5, fontWeight: 700, color: T.text }}>{c.label}</span>
              <span style={{ fontSize: 12, color: T.muted }}> — {c.detail}</span>
            </div>
          </div>
        )
      })}
    </div>
  )
}

function Row({ r, onOpen, inCmp, onCmp, open, onToggle, inTeam, onTeam }) {
  const fcLeft = Math.max(0, Math.min(60, (r.floor / (r.ceiling || 1)) * 30))
  return (
    <div style={{ background: T.card, border: `1px solid ${inCmp ? T.green : open ? T.border2 : r.rank <= 3 ? T.greenMid : T.border}`,
      borderRadius: 13, marginBottom: 8, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '11px 13px' }}>
        <button onClick={() => onToggle(r)} style={{ display: 'flex', alignItems: 'center', gap: 11, flex: 1, minWidth: 0,
          textAlign: 'left', cursor: 'pointer', color: T.text, background: 'none', border: 'none', padding: 0 }}>
          <span style={{ fontFamily: T.mono, fontWeight: 600, fontSize: 13, color: r.rank <= 3 ? T.green : T.muted, width: 20, textAlign: 'center', flexShrink: 0 }}>{r.rank}</span>
          <Avatar url={r.headshot} name={r.name} team={r.team} size={34} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 14.5, lineHeight: 1.2 }}>
              {r.name} <span style={{ color: T.muted, fontWeight: 400, fontSize: 11, whiteSpace: 'nowrap' }}>{r.pos} · {r.team}</span></div>
            <div style={{ fontSize: 11, color: T.muted, marginTop: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
              {`vs ${teamNick(r.opp)}`}
              {r.grade !== '—' && <span style={{ fontFamily: T.mono, fontWeight: 600, fontSize: 10, color: gradeCol(r.grade),
                background: 'transparent', border: `1px solid ${gradeCol(r.grade)}44`, borderRadius: 4, padding: '1px 5px' }}>
                {r.grade}{r.def_rank ? ` · #${r.def_rank}` : ''}</span>}
              <span style={{ color: T.faint }}>{open ? '▲' : '▼'}</span>
            </div>
            <div style={{ height: 4, background: T.card2, borderRadius: 2, marginTop: 6, position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: 0, bottom: 0, left: `${fcLeft}%`, right: '18%', background: T.green, opacity: 0.5, borderRadius: 2 }} />
            </div>
          </div>
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <div style={{ fontFamily: T.mono, fontWeight: 600, fontSize: 19, lineHeight: 1, color: ptsCol(r.pts) }}>{r.pts}</div>
            <div style={{ fontFamily: T.mono, fontSize: 10, color: T.faint }}>{r.floor}–{r.ceiling}</div>
            <div style={{ marginTop: 4 }}><VerdictChip label={r.verdict} level={FTSY_VLEVEL[r.verdict]} /></div>
          </div>
        </button>
        <span role="button" onClick={e => { e.stopPropagation(); onCmp(r) }} title="Start/Sit compare"
          style={{ width: 26, height: 26, borderRadius: 7, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13, fontWeight: 900, cursor: 'pointer', background: inCmp ? T.green : T.greenDim, color: inCmp ? '#04140D' : T.green,
            border: `1px solid ${T.green}` }}>{inCmp ? '✓' : '⇄'}</span>
      </div>
      {open && (
        <div style={{ padding: '2px 15px 14px', borderTop: `1px solid ${T.border}` }}>
          {/* Fantasy write-up — the analyst read in lineup language (role, matchup,
              floor/ceiling), not betting terms. */}
          {r.writeup
            ? <div style={{ fontSize: 13, lineHeight: 1.5, color: T.text, marginTop: 11 }}>{r.writeup}</div>
            : r.case_summary && <div style={{ fontSize: 12.5, fontWeight: 700, color: verdictText(r.verdict), marginTop: 11 }}>{r.case_summary}</div>}
          <CaseList items={r.case} />
          <div style={{ display: 'flex', gap: 8, marginTop: 13 }}>
            <button onClick={() => onTeam && onTeam(r)} style={{ flex: 1, padding: '9px 0', borderRadius: 9, cursor: 'pointer',
              fontWeight: 700, fontSize: 12, border: `1px solid ${T.green}`,
              background: inTeam ? T.green : T.greenDim, color: inTeam ? '#04140D' : T.green }}>
              {inTeam ? '✓ On my team' : '＋ Add to my team'}
            </button>
            <button onClick={() => onOpen(r)} style={{ flex: 1, padding: '9px 0', borderRadius: 9, cursor: 'pointer',
              fontWeight: 700, fontSize: 12, background: T.card2, color: T.green, border: `1px solid ${T.border2}` }}>
              Full breakdown ›
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function StartSit({ pair, onClear }) {
  const [a, b] = pair
  const aw = a.pts >= b.pts
  const cell = (p, win) => (
    <div style={{ flex: 1, textAlign: 'center', borderRadius: 12, padding: '11px 8px',
      background: win ? T.greenDim : T.card2, border: `1px solid ${win ? T.greenMid : T.border}` }}>
      <div style={{ fontWeight: 700, fontSize: 13.5 }}>{p.name.split(' ').slice(-1)[0]}</div>
      <div style={{ fontSize: 10.5, color: T.muted, marginTop: 2 }}>{p.pos} · {p.team} {p.pos === 'QB' ? '@' : 'vs'} {p.opp}</div>
      <div style={{ fontFamily: T.mono, fontWeight: 600, fontSize: 25, marginTop: 8, color: win ? T.green : T.text }}>{p.pts}</div>
      <div style={{ fontFamily: T.mono, fontSize: 10, color: T.faint }}>{p.floor}–{p.ceiling} · {p.grade}</div>
      {p.verdict && <div style={{ marginTop: 6 }}><VerdictChip label={p.verdict} level={FTSY_VLEVEL[p.verdict]} /></div>}
    </div>
  )
  const diff = Math.abs(a.pts - b.pts).toFixed(1)
  const win = aw ? a : b
  return (
    <div style={{ background: T.card, border: `1px solid ${T.border2}`, borderRadius: 16, padding: 14, marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 11 }}>
        <span style={{ fontSize: 13, fontWeight: 900 }}>Start / Sit</span>
        <button onClick={onClear} style={{ fontSize: 11, fontWeight: 700, color: T.muted, background: 'transparent', border: 'none', cursor: 'pointer' }}>clear</button>
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        {cell(a, aw)}<span style={{ fontWeight: 900, fontSize: 12, color: T.faint }}>VS</span>{cell(b, !aw)}
      </div>
      <div style={{ marginTop: 11, fontSize: 12.5, color: T.muted, textAlign: 'center' }}>
        Start <b style={{ color: T.green }}>{win.name.split(' ').slice(-1)[0]}</b> — +{diff} pts
        {win.grade !== '—' && (aw ? b : a).grade !== '—' && win.grade < (aw ? b : a).grade ? `, and the softer matchup (${win.grade})` : ''}.
      </div>
    </div>
  )
}

export default function FantasyTab({ season, week, onPlayer }) {
  const [mode, setMode] = useState('rankings')
  const [scoring, setScoring] = useState('ppr')
  const [pos, setPos] = useState('FLEX')
  const [rows, setRows] = useState(null)
  const [q, setQ] = useState('')
  const [cmp, setCmp] = useState([])
  const [openId, setOpenId] = useState(null)
  // "My team" — persisted so it survives reload / slate change. Built from the
  // "＋ Add to my team" button on any player's case, feeds the lineup optimizer.
  const [team, setTeam] = useState(() => { try { return JSON.parse(localStorage.getItem('brkr_nfl_team') || '[]') } catch (_) { return [] } })
  useEffect(() => { try { localStorage.setItem('brkr_nfl_team', JSON.stringify(team)) } catch (_) {} }, [team])
  const [oppTeam, setOppTeam] = useState(() => { try { return JSON.parse(localStorage.getItem('brkr_nfl_opp') || '[]') } catch (_) { return [] } })
  useEffect(() => { try { localStorage.setItem('brkr_nfl_opp', JSON.stringify(oppTeam)) } catch (_) {} }, [oppTeam])

  useEffect(() => {
    let alive = true; setRows(null); setOpenId(null)
    getNFLFantasy(season, week, scoring, pos).then(d => { if (alive) setRows(d.rows || []) })
    return () => { alive = false }
  }, [season, week, scoring, pos])

  const toggleCmp = r => setCmp(c => c.some(x => x.gsis_id === r.gsis_id)
    ? c.filter(x => x.gsis_id !== r.gsis_id) : [...c, r].slice(-2))
  const inCmp = new Set(cmp.map(x => x.gsis_id))
  const open = r => onPlayer && onPlayer({ gsis_id: r.gsis_id, full_name: r.name, pos: r.pos,
    team: r.team, opp: r.opp, headshot: r.headshot, note: `Fantasy · ${r.pts} proj pts`, status: r.status || 'active',
    // Fantasy context → the modal leads with the lineup read, not a betting card.
    fantasy: true, fantasy_pts: r.pts, fantasy_floor: r.floor, fantasy_ceiling: r.ceiling,
    fantasy_verdict: r.verdict, fantasy_writeup: r.writeup, fantasy_scoring: scoring })

  const addTeam = r => setTeam(t => t.some(x => x.gsis_id === r.gsis_id) ? t : [...t, { gsis_id: r.gsis_id, name: r.name, pos: r.pos }])
  const removeTeam = r => setTeam(t => t.filter(x => x.gsis_id !== r.gsis_id))
  // Me / Opp are mutually exclusive — adding a player to one side removes him from the
  // other, so he can never be double-counted into both totals / the win-prob bar.
  const toggleTeam = r => {
    setTeam(t => t.some(x => x.gsis_id === r.gsis_id) ? t.filter(x => x.gsis_id !== r.gsis_id) : [...t, { gsis_id: r.gsis_id, name: r.name, pos: r.pos }])
    setOppTeam(o => o.filter(x => x.gsis_id !== r.gsis_id))
  }
  const toggleOpp = r => {
    setOppTeam(t => t.some(x => x.gsis_id === r.gsis_id) ? t.filter(x => x.gsis_id !== r.gsis_id) : [...t, { gsis_id: r.gsis_id, name: r.name, pos: r.pos }])
    setTeam(o => o.filter(x => x.gsis_id !== r.gsis_id))
  }
  const inTeam = new Set(team.map(x => x.gsis_id))

  const MODES = [['rankings', 'Rankings'], ['draft', 'Draft'], ['lineup', 'Lineup'], ['matchup', 'Matchup'], ['cascade', 'Cascade ⚡']]

  return (
    <div style={{ padding: '10px 16px 90px' }}>
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, background: T.card, border: `1px solid ${T.border}`, borderRadius: 11, padding: 3 }}>
        {MODES.map(([k, l]) => (
          <button key={k} onClick={() => setMode(k)} style={{ flex: 1, padding: '8px 2px', borderRadius: 8, cursor: 'pointer',
            fontWeight: 800, fontSize: 11.5, whiteSpace: 'nowrap', border: 'none',
            background: mode === k ? T.green : 'transparent', color: mode === k ? '#04140D' : T.muted }}>{l}</button>
        ))}
      </div>

      {mode === 'cascade' ? <CascadePanel season={season} week={week} scoring={scoring} /> : (<>
      {/* scoring toggle — shared by Rankings and Lineup */}
      <div style={{ display: 'flex', gap: 6, marginBottom: mode === 'lineup' ? 12 : 9 }}>
        {SCORING.map(([k, l]) => (
          <button key={k} onClick={() => setScoring(k)} style={{ flex: 1, padding: '8px 0', borderRadius: 9, cursor: 'pointer',
            fontWeight: 800, fontSize: 12, border: `1px solid ${scoring === k ? T.green : T.border}`,
            background: scoring === k ? T.greenDim : T.card, color: scoring === k ? T.green : T.muted }}>{l}</button>
        ))}
      </div>

      {mode === 'draft' ? (
        <DraftPanel season={season} scoring={scoring} onPlayer={onPlayer} />
      ) : mode === 'lineup' ? (
        <LineupPanel season={season} week={week} scoring={scoring} team={team}
          onAddTeam={addTeam} onRemoveTeam={removeTeam} onClearTeam={() => setTeam([])} />
      ) : mode === 'matchup' ? (
        <MatchupPanel season={season} week={week} scoring={scoring} me={team} opp={oppTeam}
          onMe={toggleTeam} onOpp={toggleOpp} />
      ) : (<>
      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', marginBottom: 12, scrollbarWidth: 'none' }}>
        {POSES.map(p => (
          <button key={p} onClick={() => setPos(p)} style={{ flexShrink: 0, padding: '6px 14px', borderRadius: 20, cursor: 'pointer',
            fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap', border: `1px solid ${pos === p ? T.green : T.border}`,
            background: pos === p ? T.green : T.card, color: pos === p ? '#04140D' : T.muted }}>{p}</button>
        ))}
      </div>

      {cmp.length === 2 && <StartSit pair={cmp} onClear={() => setCmp([])} />}

      {rows?.length > 0 && <SearchBar value={q} onChange={setQ} placeholder="Search player, team, or opponent…" />}

      {!rows ? <Spinner />
        : !rows.length ? <Empty message="No fantasy projections yet — this week's board loads midweek. Check back near kickoff." />
        : (() => {
          const shown = rows.filter(searchMatch(q, r => [r.name, r.team, r.opp]))
          return (<>
          <div style={{ fontSize: 10.5, color: T.muted, marginBottom: 8 }}>
            {pos} · {scoring.toUpperCase()} · <span style={{ color: T.green }}>tap a player for the case</span> · ⇄ to compare
            {team.length > 0 && <span style={{ color: T.muted }}> · {team.length} on my team</span>}
          </div>
          {!shown.length ? <Empty message={`No players match "${q}"`} />
            : shown.map(r => <Row key={r.gsis_id} r={r} onOpen={open} inCmp={inCmp.has(r.gsis_id)} onCmp={toggleCmp}
            open={openId === r.gsis_id} onToggle={() => setOpenId(id => id === r.gsis_id ? null : r.gsis_id)}
            inTeam={inTeam.has(r.gsis_id)} onTeam={toggleTeam} />)}
        </>)})()}
      </>)}
      </>)}
    </div>
  )
}
